import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  schemas,
  UserRole,
  type ProjectDetails,
  type ProjectStatusValue,
  type UpdateProjectRequest,
} from '@kreart/shared';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Form,
  TextField,
  SelectField,
  DateField,
  TextareaField,
  FormErrorBanner,
} from '@/components/form';
import { useClients } from '@/features/clients/hooks';
import { useUsers } from '@/features/users/hooks';
import { useTransitionProject, useUpdateProject } from '@/features/projects/hooks';
import { errorKeyFor } from '@/features/projects/errorMessages';
import { PROJECT_STATUS_VALUES } from '@/features/projects/statusBadge';
import { CreatedByField } from './CreatedByField';
import { ManualPricingFields } from './ManualPricingFields';

type FormValues = {
  clientId: string;
  title: string;
  agentId: string;
  status: ProjectStatusValue;
  deadline: string | null;
  notes: string;
  rejectionDetails: string;
  manualMaterialCost: string;
  manualOfferedPrice: string;
};

interface ProjectEditDialogProps {
  open: boolean;
  project: ProjectDetails | null;
  onClose: () => void;
}

/** Non-negative, at most two decimals, or left blank. */
const money = z
  .string()
  .trim()
  .regex(/^\d*(\.\d{1,2})?$/)
  .max(32);

const editProjectSchema = z.object({
  clientId: z.string().cuid(),
  title: z.string().trim().min(1).max(200),
  agentId: z.string().cuid().or(z.literal('')),
  status: schemas.projectStatusEnum,
  deadline: z.string().datetime().nullable().or(z.literal('')),
  notes: z.string().trim().max(2000),
  rejectionDetails: z.string().trim().max(1000),
  manualMaterialCost: money,
  manualOfferedPrice: money,
});

function formValues(project: ProjectDetails): FormValues {
  return {
    clientId: project.clientId,
    title: project.title,
    agentId: project.agentId ?? '',
    status: project.status,
    deadline: project.deadline ?? '',
    notes: project.notes ?? '',
    rejectionDetails: project.rejectionDetails ?? '',
    // Absent entirely for a viewer without financial access — the section is
    // hidden for them too, so nothing they submit can clear these.
    manualMaterialCost: project.financials?.manualMaterialCost ?? '',
    manualOfferedPrice: project.financials?.manualOfferedPrice ?? '',
  };
}

function dateKey(value: string | null | undefined): string {
  return value ? (value.split('T')[0] ?? '') : '';
}

/**
 * A typed amount as the API stores it, or null when the field was left blank.
 * Normalising to two decimals is what lets "1250" be recognised as unchanged
 * from a stored "1250.00" instead of dirtying the form on every open.
 */
function moneyValue(value: string): string | null {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed.toFixed(2) : null;
}

export function ProjectEditDialog({ open, project, onClose }: ProjectEditDialogProps) {
  const { t } = useTranslation();
  const wasOpenRef = useRef(false);
  const updateMut = useUpdateProject(project?.id ?? '');
  const transitionMut = useTransitionProject(project?.id ?? '');
  const isPending = updateMut.isPending || transitionMut.isPending;

  const clientsQuery = useClients({ page: 1, pageSize: 100, includeDeleted: false });
  const clientOptions = (clientsQuery.data?.items ?? []).map((c) => ({
    value: c.id,
    label: c.name,
  }));

  const agentsQuery = useUsers({
    page: 1,
    pageSize: 100,
    role: UserRole.SALES_AGENT,
    active: true,
    includeDeleted: false,
  });
  const agentOptions = [
    { value: '', label: '—' },
    ...(agentsQuery.data?.items ?? []).map((u) => ({ value: u.id, label: u.fullName })),
  ];
  const statusOptions = PROJECT_STATUS_VALUES.map((status) => ({
    value: status,
    label: t(`projects.status.${status}`),
  }));

  const form = useForm<FormValues>({
    resolver: zodResolver(editProjectSchema),
    defaultValues: project
      ? formValues(project)
      : {
          clientId: '',
          title: '',
          agentId: '',
          status: 'IN_PROGRESS',
          deadline: '',
          notes: '',
          rejectionDetails: '',
          manualMaterialCost: '',
          manualOfferedPrice: '',
        },
  });
  const selectedStatus = useWatch({ control: form.control, name: 'status' });

  useEffect(() => {
    if (open && project && !wasOpenRef.current) {
      form.reset(formValues(project));
      updateMut.reset();
      transitionMut.reset();
    }
    wasOpenRef.current = open;
  }, [form, open, project, transitionMut, updateMut]);

  const closeIfIdle = () => {
    if (!isPending) onClose();
  };

  const onSubmit = async (values: FormValues) => {
    if (!project) return;
    const statusChanged = values.status !== project.status;
    const rejectionDetails = values.rejectionDetails.trim();
    if (statusChanged && values.status === 'REJECTED' && rejectionDetails.length < 5) {
      form.setError('rejectionDetails', { message: t('projects.errors.rejectionRequired') });
      return;
    }

    const updateBody: UpdateProjectRequest = {};
    const title = values.title.trim();
    const nextAgentId = values.agentId || null;
    const nextDeadline = values.deadline || null;
    const nextNotes = values.notes.trim() || null;

    if (title !== project.title) updateBody.title = title;
    if (values.clientId !== project.clientId) updateBody.clientId = values.clientId;
    if (nextAgentId !== project.agentId) updateBody.agentId = nextAgentId;
    if (dateKey(nextDeadline) !== dateKey(project.deadline)) updateBody.deadline = nextDeadline;
    if (nextNotes !== (project.notes ?? null)) updateBody.notes = nextNotes;
    // Only when the caller can see financials at all: otherwise the form never
    // held these figures, and sending them would wipe values it never showed.
    if (project.financials) {
      const nextMaterialCost = moneyValue(values.manualMaterialCost);
      const nextOfferedPrice = moneyValue(values.manualOfferedPrice);
      if (nextMaterialCost !== project.financials.manualMaterialCost) {
        updateBody.manualMaterialCost = nextMaterialCost;
      }
      if (nextOfferedPrice !== project.financials.manualOfferedPrice) {
        updateBody.manualOfferedPrice = nextOfferedPrice;
      }
    }

    try {
      if (Object.keys(updateBody).length > 0) {
        await updateMut.mutateAsync(updateBody);
      }
      if (statusChanged) {
        await transitionMut.mutateAsync({
          status: values.status,
          rejectionDetails: values.status === 'REJECTED' ? rejectionDetails : undefined,
        });
      }
      onClose();
    } catch {
      /* surfaced via FormErrorBanner */
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) closeIfIdle();
      }}
    >
      <DialogContent className="w-[min(94vw,640px)] max-w-none p-0">
        <DialogHeader className="mb-0 border-b border-border px-6 py-5">
          <DialogTitle className="text-xl font-semibold">{t('projects.edit.title')}</DialogTitle>
        </DialogHeader>
        <Form form={form} onSubmit={onSubmit} className="space-y-4 px-6 py-5">
          <SelectField
            name="clientId"
            label={t('projects.fields.client')}
            options={clientOptions}
            placeholder={t('projects.fields.clientPlaceholder') ?? undefined}
          />
          <TextField name="title" label={t('projects.fields.title')} autoFocus />
          <div className="grid gap-4 sm:grid-cols-2">
            <SelectField name="agentId" label={t('projects.fields.agent')} options={agentOptions} />
            <SelectField
              name="status"
              label={t('projects.fields.status')}
              options={statusOptions}
            />
          </div>
          <DateField name="deadline" label={t('projects.fields.deadline')} />
          {project && <CreatedByField fullName={project.createdByFullName} />}
          <TextareaField name="notes" label={t('projects.fields.notes')} rows={3} />
          {selectedStatus === 'REJECTED' && (
            <TextareaField
              name="rejectionDetails"
              label={t('projects.transition.rejectionPlaceholder')}
              rows={3}
            />
          )}

          {project?.financials ? (
            <ManualPricingFields currency={project.financials.currency} />
          ) : null}

          <FormErrorBanner
            error={updateMut.error ?? transitionMut.error}
            resolveKey={errorKeyFor}
          />

          <DialogFooter className="mt-0 border-t border-border px-0 pt-4">
            <Button type="button" variant="ghost" onClick={closeIfIdle} disabled={isPending}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={isPending || !project}>
              {isPending ? t('common.loading') : t('projects.edit.submit')}
            </Button>
          </DialogFooter>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
